import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import SubscriptionsRoundedIcon from "@mui/icons-material/SubscriptionsRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import ElectricalServicesRoundedIcon from "@mui/icons-material/ElectricalServicesRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import { useStyle } from "../../context/styleContext/styleContext";

const API_BASE = "https://api.parishaudio.com/api/email";

export default function EmailTesting() {
  const { style } = useStyle();

  const [targetEmail, setTargetEmail] = useState("");
  const [headline, setHeadline] = useState("A new plugin is on the way");
  const [marketingMessage, setMarketingMessage] = useState(
    "This is a sample marketing email layout for testing. Replace this copy with your real campaign when you're ready."
  );
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loadingKey, setLoadingKey] = useState("");

  const commonFieldSx = useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        color: style.color4,
        backgroundColor: "rgba(255,255,255,0.03)",
        borderRadius: "8px",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: style.color2,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: style.color3,
      },
      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: style.accentGreen,
      },
      "& .MuiInputBase-input::placeholder": {
        color: style.color3,
        opacity: 1,
      },
      "& .MuiInputLabel-root": {
        color: style.color3,
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: style.color4,
      },
      "& .MuiFormHelperText-root": {
        color: style.color3,
      },
    }),
    [style]
  );

  const emailTests = [
    {
      key: "connection",
      title: "Test SES Connection",
      description: "Verifies the Nodemailer transporter can connect and authenticate.",
      endpoint: `${API_BASE}/test-connection`,
      method: "POST",
      icon: <ElectricalServicesRoundedIcon sx={{ color: style.color4 }} />,
      buildBody: () => ({}),
      buttonLabel: "Test Connection",
    },
    {
      key: "welcome",
      title: "Welcome Email",
      description: "Sends the account welcome email from Parish Audio Accounts.",
      endpoint: `${API_BASE}/test-welcome`,
      method: "POST",
      icon: <MailOutlineRoundedIcon sx={{ color: style.color4 }} />,
      buildBody: () => ({
        email: targetEmail,
        firstName: "Nate",
        dashboardUrl: "https://parishaudio.com/account",
      }),
      buttonLabel: "Send Welcome",
    },
    {
      key: "order",
      title: "Order Confirmation",
      description: "Sends a thank-you / order confirmation email from Parish Audio Orders.",
      endpoint: `${API_BASE}/test-order`,
      method: "POST",
      icon: <ShoppingBagRoundedIcon sx={{ color: style.color4 }} />,
      buildBody: () => ({
        email: targetEmail,
        customerName: "Nate",
        orderNumber: "PA-TEST-1001",
        total: 49,
        downloadsUrl: "https://parishaudio.com/my-gear",
        items: [{ name: "D1 Delay", quantity: 1, price: 49 }],
      }),
      buttonLabel: "Send Order Email",
    },
    {
      key: "download",
      title: "Download Ready",
      description: "Sends a product download-ready email after purchase.",
      endpoint: `${API_BASE}/test-download-ready`,
      method: "POST",
      icon: <DownloadRoundedIcon sx={{ color: style.color4 }} />,
      buildBody: () => ({
        email: targetEmail,
        productName: "D1 Delay",
        downloadsUrl: "https://parishaudio.com/my-gear",
      }),
      buttonLabel: "Send Download Email",
    },
    {
      key: "subscription",
      title: "Subscription Welcome",
      description: "Sends the subscription activation email.",
      endpoint: `${API_BASE}/test-subscription-welcome`,
      method: "POST",
      icon: <SubscriptionsRoundedIcon sx={{ color: style.color4 }} />,
      buildBody: () => ({
        email: targetEmail,
        firstName: "Nate",
        manageUrl: "https://parishaudio.com/manage-subscriptions",
      }),
      buttonLabel: "Send Subscription Email",
    },
    {
      key: "subscription-canceled",
      title: "Subscription Canceled",
      description: "Sends the subscription canceled email with the current access end date.",
      endpoint: `${API_BASE}/test-subscription-canceled`,
      method: "POST",
      icon: <CancelRoundedIcon sx={{ color: style.color4 }} />,
      buildBody: () => ({
        email: targetEmail,
        firstName: "Nate",
        accessEndDate: new Date(
          Date.now() + 7 * 24 * 60 * 60 * 1000
        ).toISOString(),
        reactivateUrl: "https://parishaudio.com/manage-subscriptions",
      }),
      buttonLabel: "Send Canceled Email",
    },
    {
      key: "subscription-reactivated",
      title: "Subscription Reactivated",
      description: "Sends the subscription reactivated email.",
      endpoint: `${API_BASE}/test-subscription-reactivated`,
      method: "POST",
      icon: <AutorenewRoundedIcon sx={{ color: style.color4 }} />,
      buildBody: () => ({
        email: targetEmail,
        firstName: "Nate",
        manageUrl: "https://parishaudio.com/manage-subscriptions",
      }),
      buttonLabel: "Send Reactivated Email",
    },
    {
      key: "marketing",
      title: "Marketing Test",
      description: "Sends the sample news / campaign email from Parish Audio News.",
      endpoint: `${API_BASE}/test-marketing`,
      method: "POST",
      icon: <CampaignRoundedIcon sx={{ color: style.color4 }} />,
      buildBody: () => ({
        email: targetEmail,
        headline,
        message: marketingMessage,
        ctaLabel: "Browse Plugins",
        ctaUrl: "https://parishaudio.com/plugins",
        unsubscribeUrl: "https://parishaudio.com/unsubscribe",
      }),
      buttonLabel: "Send Marketing Email",
    },
    {
      key: "password-reset",
      title: "Password Reset",
      description: "Triggers the forgot-password flow and sends the reset email if the account exists.",
      endpoint: `${API_BASE}/forgot-password`,
      method: "POST",
      icon: <SecurityRoundedIcon sx={{ color: style.color4 }} />,
      buildBody: () => ({
        email: targetEmail,
      }),
      buttonLabel: "Send Password Reset",
    },
  ];

  async function sendTest(test) {
    if (test.key !== "connection" && !targetEmail.trim()) {
      setStatus({
        type: "error",
        message: "Please enter a target email address first.",
      });
      return;
    }

    setLoadingKey(test.key);
    setStatus({ type: "", message: "" });

    try {
      const response = await fetch(test.endpoint, {
        method: test.method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(test.buildBody()),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.message || data?.error || `Request failed with status ${response.status}`);
      }

      setStatus({
        type: "success",
        message: data?.message || `${test.title} sent successfully.`,
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || `Failed to send ${test.title}.`,
      });
    } finally {
      setLoadingKey("");
    }
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw",
        overflowX: "hidden",
        backgroundColor: "black",
        px: { xs: 1.25, sm: 1.5, md: 2.5 },
        py: { xs: 1.25, md: 2 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "1120px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              minWidth: 0,
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                px: { xs: 1.25, sm: 1.5, md: 2 },
                pt: { xs: 1.25, md: 1.5 },
                pb: { xs: 1, md: 1.25 },
                backgroundColor: style.darkPanel,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Stack spacing={1.1}>
                <Chip
                  icon={<MailOutlineRoundedIcon />}
                  label="Admin Email Tools"
                  sx={{
                    alignSelf: "flex-start",
                    borderRadius: "5px",
                    fontWeight: 700,
                    color: style.color4,
                    backgroundColor: "rgba(255,255,255,0.06)",
                    height: 28,
                  }}
                />

                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "1.75rem", md: "2.3rem" },
                    fontWeight: 800,
                    lineHeight: 1.05,
                    letterSpacing: "-0.03em",
                    color: style.color4,
                  }}
                >
                  Email Test Dashboard
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: style.color3,
                    maxWidth: "760px",
                    fontSize: { xs: "0.95rem", md: "1rem" },
                    lineHeight: 1.65,
                  }}
                >
                  Send test emails through your Parish Audio API endpoints and verify
                  each template, sender address, and delivery path.
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: style.darkPanel,
                  borderRadius: "14px",
                  p: { xs: 1.1, md: 1.3 },
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    label="Target Email Address"
                    value={targetEmail}
                    onChange={(e) => setTargetEmail(e.target.value)}
                    placeholder="yourpersonalemail@example.com"
                    sx={commonFieldSx}
                    helperText="All test emails except the transporter test use this address."
                  />

                  <TextField
                    fullWidth
                    label="Marketing Test Headline"
                    value={headline}
                    onChange={(e) => setHeadline(e.target.value)}
                    sx={commonFieldSx}
                  />

                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Marketing Test Message"
                    value={marketingMessage}
                    onChange={(e) => setMarketingMessage(e.target.value)}
                    sx={commonFieldSx}
                  />
                </Stack>
              </Paper>
            </Box>
          </Paper>

          {status.message ? (
            <Alert
              severity={status.type === "error" ? "error" : "success"}
              sx={{
                borderRadius: "12px",
                backgroundColor:
                  status.type === "error"
                    ? "rgba(140, 30, 30, 0.25)"
                    : "rgba(38, 166, 104, 0.18)",
                color: style.color4,
                border: "1px solid rgba(255,255,255,0.08)",
                "& .MuiAlert-icon": {
                  color: style.color4,
                },
              }}
            >
              {status.message}
            </Alert>
          ) : null}

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              minWidth: 0,
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
              <Stack spacing={1.25}>
                {emailTests.map((test, index) => (
                  <Paper
                    key={test.key}
                    elevation={0}
                    sx={{
                      backgroundColor: style.darkPanel,
                      borderRadius: "16px",
                      p: { xs: 1.15, md: 1.3 },
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1.25}
                      alignItems={{ xs: "flex-start", md: "center" }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1.2} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255,255,255,0.05)",
                            border: "1px solid rgba(255,255,255,0.06)",
                            flexShrink: 0,
                          }}
                        >
                          {test.icon}
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              color: style.color4,
                              fontWeight: 800,
                              fontSize: "1.02rem",
                              lineHeight: 1.2,
                              mb: 0.4,
                            }}
                          >
                            {test.title}
                          </Typography>
                          <Typography
                            sx={{
                              color: style.color3,
                              fontSize: "0.92rem",
                              lineHeight: 1.6,
                              maxWidth: "760px",
                            }}
                          >
                            {test.description}
                          </Typography>
                          <Typography
                            sx={{
                              color: style.color3,
                              fontSize: "0.78rem",
                              lineHeight: 1.5,
                              mt: 0.6,
                              wordBreak: "break-all",
                            }}
                          >
                            Endpoint: {test.endpoint}
                          </Typography>
                        </Box>
                      </Stack>

                      <Button
                        variant="contained"
                        onClick={() => sendTest(test)}
                        disabled={loadingKey === test.key}
                        startIcon={
                          loadingKey === test.key ? (
                            <CircularProgress size={16} sx={{ color: style.color4 }} />
                          ) : (
                            <SendRoundedIcon />
                          )
                        }
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 800,
                          color: style.color4,
                          backgroundColor: style.accentGreen,
                          minWidth: { xs: "100%", md: 210 },
                          minHeight: 46,
                          alignSelf: { xs: "stretch", md: "center" },
                          "&:hover": {
                            backgroundColor: style.accentGreen,
                            filter: "brightness(1.05)",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "rgba(255,255,255,0.12)",
                            color: style.color3,
                          },
                        }}
                      >
                        {loadingKey === test.key ? "Sending..." : test.buttonLabel}
                      </Button>
                    </Stack>

                    {index < emailTests.length - 1 ? (
                      <Divider sx={{ borderColor: "transparent", mt: 0.5 }} />
                    ) : null}
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}

// import React, { useMemo, useState } from "react";
// import {
//   Alert,
//   Box,
//   Button,
//   Chip,
//   CircularProgress,
//   Divider,
//   Paper,
//   Stack,
//   TextField,
//   Typography,
// } from "@mui/material";
// import SendRoundedIcon from "@mui/icons-material/SendRounded";
// import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
// import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
// import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
// import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
// import SubscriptionsRoundedIcon from "@mui/icons-material/SubscriptionsRounded";
// import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
// import ElectricalServicesRoundedIcon from "@mui/icons-material/ElectricalServicesRounded";
// import { useStyle } from "../../context/styleContext/styleContext";

// const API_BASE = "https://api.parishaudio.com/api/email";

// export default function EmailTesting() {
//   const { style } = useStyle();

//   const [targetEmail, setTargetEmail] = useState("");
//   const [headline, setHeadline] = useState("A new plugin is on the way");
//   const [marketingMessage, setMarketingMessage] = useState(
//     "This is a sample marketing email layout for testing. Replace this copy with your real campaign when you're ready."
//   );
//   const [status, setStatus] = useState({ type: "", message: "" });
//   const [loadingKey, setLoadingKey] = useState("");

//   const commonFieldSx = useMemo(
//     () => ({
//       "& .MuiOutlinedInput-root": {
//         color: style.color4,
//         backgroundColor: "rgba(255,255,255,0.03)",
//         borderRadius: "8px",
//       },
//       "& .MuiOutlinedInput-notchedOutline": {
//         borderColor: style.color2,
//       },
//       "&:hover .MuiOutlinedInput-notchedOutline": {
//         borderColor: style.color3,
//       },
//       "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
//         borderColor: style.accentGreen,
//       },
//       "& .MuiInputBase-input::placeholder": {
//         color: style.color3,
//         opacity: 1,
//       },
//       "& .MuiInputLabel-root": {
//         color: style.color3,
//       },
//       "& .MuiInputLabel-root.Mui-focused": {
//         color: style.color4,
//       },
//       "& .MuiFormHelperText-root": {
//         color: style.color3,
//       },
//     }),
//     [style]
//   );

//   const emailTests = [
//     {
//       key: "connection",
//       title: "Test SES Connection",
//       description: "Verifies the Nodemailer transporter can connect and authenticate.",
//       endpoint: `${API_BASE}/test-connection`,
//       method: "POST",
//       icon: <ElectricalServicesRoundedIcon sx={{ color: style.color4 }} />,
//       buildBody: () => ({}),
//       buttonLabel: "Test Connection",
//     },
//     {
//       key: "welcome",
//       title: "Welcome Email",
//       description: "Sends the account welcome email from Parish Audio Accounts.",
//       endpoint: `${API_BASE}/test-welcome`,
//       method: "POST",
//       icon: <MailOutlineRoundedIcon sx={{ color: style.color4 }} />,
//       buildBody: () => ({
//         email: targetEmail,
//         firstName: "Nate",
//         dashboardUrl: "https://parishaudio.com/account",
//       }),
//       buttonLabel: "Send Welcome",
//     },
//     {
//       key: "order",
//       title: "Order Confirmation",
//       description: "Sends a thank-you / order confirmation email from Parish Audio Orders.",
//       endpoint: `${API_BASE}/test-order`,
//       method: "POST",
//       icon: <ShoppingBagRoundedIcon sx={{ color: style.color4 }} />,
//       buildBody: () => ({
//         email: targetEmail,
//         customerName: "Nate",
//         orderNumber: "PA-TEST-1001",
//         total: 49,
//         downloadsUrl: "https://parishaudio.com/my-gear",
//         items: [{ name: "D1 Delay", quantity: 1, price: 49 }],
//       }),
//       buttonLabel: "Send Order Email",
//     },
//     {
//       key: "download",
//       title: "Download Ready",
//       description: "Sends a product download-ready email after purchase.",
//       endpoint: `${API_BASE}/test-download-ready`,
//       method: "POST",
//       icon: <DownloadRoundedIcon sx={{ color: style.color4 }} />,
//       buildBody: () => ({
//         email: targetEmail,
//         productName: "D1 Delay",
//         downloadsUrl: "https://parishaudio.com/my-gear",
//       }),
//       buttonLabel: "Send Download Email",
//     },
//     {
//       key: "subscription",
//       title: "Subscription Welcome",
//       description: "Sends the subscription activation email.",
//       endpoint: `${API_BASE}/test-subscription-welcome`,
//       method: "POST",
//       icon: <SubscriptionsRoundedIcon sx={{ color: style.color4 }} />,
//       buildBody: () => ({
//         email: targetEmail,
//         firstName: "Nate",
//         manageUrl: "https://parishaudio.com/manage-subscriptions",
//       }),
//       buttonLabel: "Send Subscription Email",
//     },
//     {
//       key: "marketing",
//       title: "Marketing Test",
//       description: "Sends the sample news / campaign email from Parish Audio News.",
//       endpoint: `${API_BASE}/test-marketing`,
//       method: "POST",
//       icon: <CampaignRoundedIcon sx={{ color: style.color4 }} />,
//       buildBody: () => ({
//         email: targetEmail,
//         headline,
//         message: marketingMessage,
//         ctaLabel: "Browse Plugins",
//         ctaUrl: "https://parishaudio.com/plugins",
//         unsubscribeUrl: "https://parishaudio.com/unsubscribe",
//       }),
//       buttonLabel: "Send Marketing Email",
//     },
//     {
//       key: "password-reset",
//       title: "Password Reset",
//       description: "Triggers the forgot-password flow and sends the reset email if the account exists.",
//       endpoint: `${API_BASE}/forgot-password`,
//       method: "POST",
//       icon: <SecurityRoundedIcon sx={{ color: style.color4 }} />,
//       buildBody: () => ({
//         email: targetEmail,
//       }),
//       buttonLabel: "Send Password Reset",
//     },
//   ];

//   async function sendTest(test) {
//     if (test.key !== "connection" && !targetEmail.trim()) {
//       setStatus({
//         type: "error",
//         message: "Please enter a target email address first.",
//       });
//       return;
//     }

//     setLoadingKey(test.key);
//     setStatus({ type: "", message: "" });

//     try {
//       const response = await fetch(test.endpoint, {
//         method: test.method,
//         headers: {
//           "Content-Type": "application/json",
//         },
//         credentials: "include",
//         body: JSON.stringify(test.buildBody()),
//       });

//       const data = await response.json().catch(() => ({}));

//       if (!response.ok) {
//         throw new Error(data?.message || `Request failed with status ${response.status}`);
//       }

//       setStatus({
//         type: "success",
//         message: data?.message || `${test.title} sent successfully.`,
//       });
//     } catch (error) {
//       setStatus({
//         type: "error",
//         message: error.message || `Failed to send ${test.title}.`,
//       });
//     } finally {
//       setLoadingKey("");
//     }
//   }

//   return (
//     <Box
//       sx={{
//         width: "100%",
//         maxWidth: "100vw",
//         overflowX: "hidden",
//         backgroundColor: "black",
//         px: { xs: 1.25, sm: 1.5, md: 2.5 },
//         py: { xs: 1.25, md: 2 },
//         boxSizing: "border-box",
//       }}
//     >
//       <Box
//         sx={{
//           width: "100%",
//           maxWidth: "1120px",
//           margin: "0 auto",
//           display: "flex",
//           flexDirection: "column",
//           minWidth: 0,
//         }}
//       >
//         <Stack spacing={2}>
//           <Paper
//             elevation={0}
//             sx={{
//               width: "100%",
//               minWidth: 0,
//               borderRadius: "20px",
//               overflow: "hidden",
//               backgroundColor: style.color1,
//               border: `1px solid ${style.color2}`,
//               boxSizing: "border-box",
//             }}
//           >
//             <Box
//               sx={{
//                 px: { xs: 1.25, sm: 1.5, md: 2 },
//                 pt: { xs: 1.25, md: 1.5 },
//                 pb: { xs: 1, md: 1.25 },
//                 backgroundColor: style.darkPanel,
//                 borderBottom: "1px solid rgba(255,255,255,0.08)",
//               }}
//             >
//               <Stack spacing={1.1}>
//                 <Chip
//                   icon={<MailOutlineRoundedIcon />}
//                   label="Admin Email Tools"
//                   sx={{
//                     alignSelf: "flex-start",
//                     borderRadius: "5px",
//                     fontWeight: 700,
//                     color: style.color4,
//                     backgroundColor: "rgba(255,255,255,0.06)",
//                     height: 28,
//                   }}
//                 />

//                 <Typography
//                   variant="h1"
//                   sx={{
//                     fontSize: { xs: "1.75rem", md: "2.3rem" },
//                     fontWeight: 800,
//                     lineHeight: 1.05,
//                     letterSpacing: "-0.03em",
//                     color: style.color4,
//                   }}
//                 >
//                   Email Test Dashboard
//                 </Typography>

//                 <Typography
//                   variant="body1"
//                   sx={{
//                     color: style.color3,
//                     maxWidth: "760px",
//                     fontSize: { xs: "0.95rem", md: "1rem" },
//                     lineHeight: 1.65,
//                   }}
//                 >
//                   Send test emails through your Parish Audio API endpoints and verify
//                   each template, sender address, and delivery path.
//                 </Typography>
//               </Stack>
//             </Box>

//             <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
//               <Paper
//                 elevation={0}
//                 sx={{
//                   backgroundColor: style.darkPanel,
//                   borderRadius: "14px",
//                   p: { xs: 1.1, md: 1.3 },
//                   border: "1px solid rgba(255,255,255,0.08)",
//                 }}
//               >
//                 <Stack spacing={1.5}>
//                   <TextField
//                     fullWidth
//                     label="Target Email Address"
//                     value={targetEmail}
//                     onChange={(e) => setTargetEmail(e.target.value)}
//                     placeholder="yourpersonalemail@example.com"
//                     sx={commonFieldSx}
//                     helperText="All test emails except the transporter test use this address."
//                   />

//                   <TextField
//                     fullWidth
//                     label="Marketing Test Headline"
//                     value={headline}
//                     onChange={(e) => setHeadline(e.target.value)}
//                     sx={commonFieldSx}
//                   />

//                   <TextField
//                     fullWidth
//                     multiline
//                     minRows={4}
//                     label="Marketing Test Message"
//                     value={marketingMessage}
//                     onChange={(e) => setMarketingMessage(e.target.value)}
//                     sx={commonFieldSx}
//                   />
//                 </Stack>
//               </Paper>
//             </Box>
//           </Paper>

//           {status.message ? (
//             <Alert
//               severity={status.type === "error" ? "error" : "success"}
//               sx={{
//                 borderRadius: "12px",
//                 backgroundColor:
//                   status.type === "error"
//                     ? "rgba(140, 30, 30, 0.25)"
//                     : "rgba(38, 166, 104, 0.18)",
//                 color: style.color4,
//                 border: "1px solid rgba(255,255,255,0.08)",
//                 "& .MuiAlert-icon": {
//                   color: style.color4,
//                 },
//               }}
//             >
//               {status.message}
//             </Alert>
//           ) : null}

//           <Paper
//             elevation={0}
//             sx={{
//               width: "100%",
//               minWidth: 0,
//               borderRadius: "20px",
//               overflow: "hidden",
//               backgroundColor: style.color1,
//               border: `1px solid ${style.color2}`,
//               boxSizing: "border-box",
//             }}
//           >
//             <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
//               <Stack spacing={1.25}>
//                 {emailTests.map((test, index) => (
//                   <Paper
//                     key={test.key}
//                     elevation={0}
//                     sx={{
//                       backgroundColor: style.darkPanel,
//                       borderRadius: "16px",
//                       p: { xs: 1.15, md: 1.3 },
//                       border: "1px solid rgba(255,255,255,0.08)",
//                     }}
//                   >
//                     <Stack
//                       direction={{ xs: "column", md: "row" }}
//                       spacing={1.25}
//                       alignItems={{ xs: "flex-start", md: "center" }}
//                       justifyContent="space-between"
//                     >
//                       <Stack direction="row" spacing={1.2} alignItems="flex-start">
//                         <Box
//                           sx={{
//                             width: 42,
//                             height: 42,
//                             borderRadius: "10px",
//                             display: "flex",
//                             alignItems: "center",
//                             justifyContent: "center",
//                             backgroundColor: "rgba(255,255,255,0.05)",
//                             border: "1px solid rgba(255,255,255,0.06)",
//                             flexShrink: 0,
//                           }}
//                         >
//                           {test.icon}
//                         </Box>

//                         <Box>
//                           <Typography
//                             sx={{
//                               color: style.color4,
//                               fontWeight: 800,
//                               fontSize: "1.02rem",
//                               lineHeight: 1.2,
//                               mb: 0.4,
//                             }}
//                           >
//                             {test.title}
//                           </Typography>
//                           <Typography
//                             sx={{
//                               color: style.color3,
//                               fontSize: "0.92rem",
//                               lineHeight: 1.6,
//                               maxWidth: "760px",
//                             }}
//                           >
//                             {test.description}
//                           </Typography>
//                           <Typography
//                             sx={{
//                               color: style.color3,
//                               fontSize: "0.78rem",
//                               lineHeight: 1.5,
//                               mt: 0.6,
//                               wordBreak: "break-all",
//                             }}
//                           >
//                             Endpoint: {test.endpoint}
//                           </Typography>
//                         </Box>
//                       </Stack>

//                       <Button
//                         variant="contained"
//                         onClick={() => sendTest(test)}
//                         disabled={loadingKey === test.key}
//                         startIcon={
//                           loadingKey === test.key ? (
//                             <CircularProgress size={16} sx={{ color: style.color4 }} />
//                           ) : (
//                             <SendRoundedIcon />
//                           )
//                         }
//                         sx={{
//                           borderRadius: "5px",
//                           textTransform: "none",
//                           fontWeight: 800,
//                           color: style.color4,
//                           backgroundColor: style.accentGreen,
//                           minWidth: { xs: "100%", md: 210 },
//                           minHeight: 46,
//                           alignSelf: { xs: "stretch", md: "center" },
//                           "&:hover": {
//                             backgroundColor: style.accentGreen,
//                             filter: "brightness(1.05)",
//                           },
//                           "&.Mui-disabled": {
//                             backgroundColor: "rgba(255,255,255,0.12)",
//                             color: style.color3,
//                           },
//                         }}
//                       >
//                         {loadingKey === test.key ? "Sending..." : test.buttonLabel}
//                       </Button>
//                     </Stack>

//                     {index < emailTests.length - 1 ? (
//                       <Divider sx={{ borderColor: "transparent", mt: 0.5 }} />
//                     ) : null}
//                   </Paper>
//                 ))}
//               </Stack>
//             </Box>
//           </Paper>
//         </Stack>
//       </Box>
//     </Box>
//   );
// }
